import React from "react";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import AccordionSummary from "@mui/material/AccordionSummary";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from '@directv-digital/dds.ui.dtv-text';
import CardMedia from "@mui/material/CardMedia";
import DtvTheme, { colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import Tooltip from "@directv-digital/fusion.molecules.tooltip";
import { useLocationHref } from "src/hooks/useLocationHref";

const bps = DtvTheme.breakpoints;

interface CategoryHeaderProps {
    id?: string;
    index?: number;
    ariaControls?: string;
    accordionHeading?: {
        backgroundColor?: string;
        color?: string;
        content?: string;
        tooltip?: string;
    };
    expanded?: boolean;
    handleClick?: (index: number) => void;
    headerHeight?: string | number;
}

const CategoryExpandIcon = ({ accOpen, iconColor }) => (
    <CardMedia
        alt="Click to toggle Category section"
        component="img"
        src="https://www.directv.com/dtvassets/global/icons/2022/arrow-down_dk-bg.svg"
        style={{
            height: "16px",
            width: "16px",
            ...accOpen && { transform: "rotate(180deg)" }
        }}
        sx={{
            [bps.up(bps.values.sm)]: {
                height: "20px",
                width: "20px"
            },
            color: iconColor,
            filter: "invert(100%) sepia(0%) saturate(7500%) hue-rotate(351deg) brightness(106%) contrast(104%)",
            position: "relative",
            cursor: "pointer",
            margin: 0,
            transition: "transform 200ms cubic-bezier(0.42, 0, 0.58, 1) 150ms"
        }}
    />
);

export const CategoryHeader = (props: CategoryHeaderProps) => {
  const locationHrefUrl = useLocationHref();
  try {
    const { id, index, ariaControls, accordionHeading, expanded, handleClick, headerHeight } = props;
    return (
            <AccordionSummary
                aria-controls={ariaControls}
                aria-label={`${expanded ? "open" : "close"} - ${accordionHeading?.content?.toLocaleLowerCase?.()}`}
                data-linkdestination= {locationHrefUrl}
                data-linkposition="body"
                id={id}
                style={{
                    position: "sticky",
                    top: headerHeight,
                    left: 0
                }}
                sx={{
                    minHeight: "48px !important",
                    height: "48px !important",
                    backgroundColor: "rgba(228, 228, 228, 1)",
                    color: accordionHeading?.color || "#000000",
                    padding: "12px 20px",
                    zIndex: 100
                }}
                onClick={() => handleClick(index)}
            >
                <DtvBox sx={{
 display: "flex",
alignItems: "center",
width: "100%",
gap: 1,
flexWrap: "nowrap"
}}>
                    <DtvText
                        content={accordionHeading?.content || "Category Header"}
                        variant={"body1medium"}
                        sx={{
                            fontSize: "16px !important"
                        }}
                    />
                    {accordionHeading?.tooltip && (
                        <DtvBox
                            // prevent tooltip click from toggling the accordion
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center'
                            }}
                            onClick={e => {
                                e.stopPropagation();
                            }}
                            onKeyDown={e => {
                                // prevent Space/Enter on the tooltip from toggling the accordion
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.stopPropagation();
                                }
                            }}>
                        <Tooltip
                            content={accordionHeading.tooltip}
                            placement="top"
                            PopperProps={{
                                sx: {
                                    "& .MuiTypography-root": {
                                        fontFamily: `roboto-regular`,
                                        fontSize: "14px"
                                    }
                                }
                            }}
                        />
                    </DtvBox>
                    )}
                </DtvBox>
                <DtvBox sx={{
                    paddingRight: {
 xs: "4px",
sm: "12px",
md: "12px"
},
                    alignSelf: "flex-start"
                }}>
                    <CategoryExpandIcon
                        accOpen={expanded}
                        iconColor={colorPalette.black.main}
                    />
                </DtvBox>
            </AccordionSummary>
    )
  } catch (error: any) {
    console.error("Error in compare packages Tool - id >> ", error);
    return <ErrorDisplay component="compare packages Tool -> CategoryHeader" error={error} />;
  }
}
